import { useQueryClient } from '@tanstack/react-query';
import { UserKeys } from '../../../../api/users-api';
import { WelcomeTemplateEntity } from '../../../../../types';
import { UserFormValues } from '../user-schema';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { isFeatureEnabled } from '../../../../utils/feature-flags';
import SharedReviewLayoutNew, {
  ReviewSection,
} from '../../../../shared/shared-review-layout-new';
import ProfilePhotoAvatar from './profile-photo-avatar';

interface StepSignatureTemplateReviewProps {
  selectedUser: UserFormValues;
  templates: WelcomeTemplateEntity[];
  profilePhoto?: File | null;
}

function capitalize(word: string) {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

export default function StepReview({
  selectedUser,
  profilePhoto = null,
}: StepSignatureTemplateReviewProps) {
  const { features } = useUserSettings();
  const isIntegrationsEnabled = isFeatureEnabled('slackIntegration', features);
  const queryClient = useQueryClient();
  const slackChannelsData = queryClient.getQueryData<{
    pages?: { channels?: { id: string; name: string }[] }[];
  }>([UserKeys.SlackChannels]);
  const allSlackChannels =
    slackChannelsData?.pages?.flatMap((page) => page.channels || []) || [];
  const userEmail = `${selectedUser.userName}@${selectedUser.domain}`;
  const hasGroups = !!(selectedUser.groups && selectedUser.groups.length > 0);
  const hasPolicies = !!(
    selectedUser.policies && selectedUser.policies.length > 0
  );
  const hasIntegrations =
    isIntegrationsEnabled &&
    !!(selectedUser.slackChannels && selectedUser.slackChannels.length > 0);
  const hasRightSection = hasGroups || hasPolicies || hasIntegrations;

  const leftSections: ReviewSection[] = [
    // Setup User Section
    {
      title: 'Setup User',
      leftWidth: {
        labelWidth: hasRightSection ? 7.5 : 4,
        valueWidth: hasRightSection ? 4.5 : 8,
      },
      leftItems: [
        {
          label: 'User Email:',
          value: [{ email: userEmail, type: 'User' }],
          variant: 'chip',
        },
        { label: 'First Name:', value: selectedUser?.name?.givenName },
        { label: 'Last Name:', value: selectedUser?.name?.familyName },
        {
          label: 'Require user to change password at next sign-in:',
          value: selectedUser.changePasswordAtNextLogin,
        },
        {
          label: 'Profile Picture:',
          value: <ProfilePhotoAvatar file={profilePhoto} />,
          isVisible: !!profilePhoto,
        },

        {
          label: 'Organization Unit:',
          value: [
            {
              email: selectedUser.orgUnitPath || '',
              type: 'OrgUnit' as const,
            },
          ],
          variant: 'chip' as const,
        },
        {
          label: 'cPanel Role:',
          value: selectedUser.cPanelRole
            ? capitalize(selectedUser.cPanelRole)
            : '-',
        },
        {
          label: 'gPanel Role:',
          value: selectedUser.gPanelRole || '-',
        },
        {
          label: `Automatically share ${
            selectedUser?.name?.givenName || 'user'
          }'s contact information:`,
          value: selectedUser.includeInGlobalAddressList,
        },
      ],
    },
  ];

  const righSections: ReviewSection[] = [
    // Groups Section
    {
      title: 'Groups',
      leftItems: [
        {
          value:
            selectedUser.groups && selectedUser.groups.length > 0
              ? selectedUser.groups.map((target) => ({
                  email: target.groupId,
                  type: 'Group' as const,
                }))
              : '',
          variant: 'chip',
          isVisible: hasGroups,
        },
      ],
    },
    // Policies Section
    {
      title: 'Policies',
      leftItems: [
        {
          value:
            selectedUser.policies && selectedUser.policies.length > 0
              ? selectedUser.policies.map((value) => ({
                  email: `${value.policyName}`,
                  type: 'Unknown' as const,
                }))
              : '',
          variant: 'chip',
          isVisible: hasPolicies,
        },
      ],
    },

    // Integrations Section
    {
      title: 'Integrations',
      leftItems: [
        {
          label: 'Slack Channels',
          value:
            selectedUser.slackChannels && selectedUser.slackChannels.length > 0
              ? selectedUser.slackChannels.map((channelId) => {
                  const channel = allSlackChannels.find(
                    (c) => c.id === channelId
                  );
                  return {
                    email: channel ? channel.name : channelId,
                    type: 'Unknown' as const,
                  };
                })
              : '',
          variant: 'chip',
          isVisible: hasIntegrations,
        },
      ],
    },
  ];

  return (
    <SharedReviewLayoutNew
      leftSections={leftSections}
      rightSections={hasRightSection ? righSections : undefined}
    />
  );
}
